import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link2, QrCode, Copy, Check, RefreshCw, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { api, ApiCallError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type Method = 'link' | 'qr' | 'qr_pix';

interface CobroResult {
  id: string;
  paymentUrl: string;
  qrCodeData: string;
  expiresAt: string;
  method: Method;
  amount: number;
}

const toNum = (s: string): number => Number(s.replace(/[^\d]/g, '')) || 0;
const fmtGs = (n: number): string => `Gs ${Math.round(n).toLocaleString('es-PY')}`;

export function AppCobroCreateDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<Method>('qr');
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [result, setResult] = useState<CobroResult | null>(null);
  const [copied, setCopied] = useState(false);

  const reset = () => {
    setAmount('');
    setMethod('qr');
    setClientName('');
    setClientEmail('');
    setResult(null);
    setCopied(false);
    setMaxedOut(false);
  };

  const create = useMutation({
    mutationFn: () =>
      api<CobroResult>('/api/app/cobros', {
        method: 'POST',
        body: JSON.stringify({
          amount: toNum(amount),
          paymentMethod: method,
          clientName: clientName.trim() || null,
          clientEmail: method === 'link' && clientEmail.trim() ? clientEmail.trim() : null,
        }),
      }),
    onSuccess: (r) => {
      setResult(r);
      qc.invalidateQueries({ queryKey: ['app-cobros'] });
    },
    onError: (e) =>
      toast.error(e instanceof ApiCallError ? (e.body.message ?? e.body.error) : 'No se pudo generar el cobro'),
  });

  const [maxedOut, setMaxedOut] = useState(false);
  const regenerate = useMutation({
    mutationFn: (id: string) =>
      api<{ ok: true; qrCodeData: string; paymentUrl: string; expiresAt: string; regenCount: number }>(
        `/api/app/cobros/${id}/regenerate`,
        { method: 'POST' }
      ),
    onSuccess: (r) => {
      setResult((prev) => (prev ? { ...prev, ...r } : prev));
      toast.success('QR regenerado');
    },
    onError: (e) => {
      if (e instanceof ApiCallError && e.body.error === 'max_reached') {
        setMaxedOut(true);
        toast.error('Llegaste al máximo de regeneraciones. Generá un cobro nuevo.');
        return;
      }
      toast.error('No se pudo regenerar');
    },
  });

  const close = (o: boolean) => {
    if (!o) reset();
    onOpenChange(o);
  };

  const copyLink = () => {
    if (!result) return;
    void navigator.clipboard.writeText(result.paymentUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const isQr = result?.method === 'qr' || result?.method === 'qr_pix';
  const canSubmit = toNum(amount) > 0 && !create.isPending;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nuevo cobro</DialogTitle>
          <DialogDescription>
            Generá un link de pago o un QR para cobrarle a tu cliente.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <FieldGroup className="py-2">
            <Field>
              <FieldLabel htmlFor="cb-amount">Monto a cobrar (Gs)</FieldLabel>
              <Input
                id="cb-amount"
                inputMode="numeric"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="350000"
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="cb-method">Forma de pago</FieldLabel>
              <Select value={method} onValueChange={(v) => setMethod(v as Method)}>
                <SelectTrigger id="cb-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="qr">QR</SelectItem>
                  <SelectItem value="qr_pix">QR PIX</SelectItem>
                  <SelectItem value="link">Link de pago</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <FieldLabel htmlFor="cb-client">Cliente (opcional)</FieldLabel>
              <Input
                id="cb-client"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="Nombre del cliente"
              />
            </Field>
            {method === 'link' && (
              <Field>
                <FieldLabel htmlFor="cb-email">Email del cliente (opcional)</FieldLabel>
                <Input
                  id="cb-email"
                  type="email"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  placeholder="Para enviarle el link automáticamente"
                />
              </Field>
            )}
          </FieldGroup>
        ) : (
          <div className="space-y-4 py-2">
            <div className="rounded-lg border border-border bg-muted/30 p-4 text-center">
              <div className="text-xs text-muted-foreground">Cobro generado</div>
              <div className="text-2xl font-bold tabular-nums">{fmtGs(result.amount)}</div>
            </div>

            {isQr && (
              <div className="flex flex-col items-center gap-2">
                <img
                  src={`/api/app/cobros/${result.id}/qr.png?v=${encodeURIComponent(result.expiresAt)}`}
                  alt="QR de cobro"
                  className="size-48 rounded-lg border border-border bg-white p-2"
                />
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Clock className="size-3.5" />
                  Válido hasta {new Date(result.expiresAt).toLocaleTimeString('es-PY', { hour: '2-digit', minute: '2-digit' })}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={regenerate.isPending || maxedOut}
                  onClick={() => regenerate.mutate(result.id)}
                >
                  <RefreshCw className={regenerate.isPending ? 'animate-spin' : ''} />
                  {maxedOut ? 'Máximo alcanzado' : 'Regenerar QR'}
                </Button>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Input readOnly value={result.paymentUrl} className="font-mono text-xs" />
              <Button variant="outline" size="icon" onClick={copyLink} aria-label="Copiar link">
                {copied ? <Check className="size-4 text-success" /> : <Copy className="size-4" />}
              </Button>
            </div>
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={() => close(false)} disabled={create.isPending}>
                Cancelar
              </Button>
              <Button onClick={() => create.mutate()} disabled={!canSubmit}>
                {method === 'link' ? <Link2 /> : <QrCode />}
                {create.isPending ? 'Generando…' : 'Generar cobro'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={reset}>
                Otro cobro
              </Button>
              <Button onClick={() => close(false)}>Listo</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
